// src/components/IntakeForm.tsx
import React, { useState, useEffect } from 'react';
import { CheckCircle2, AlertTriangle, Scissors, Loader2, Copy } from 'lucide-react';
import { db, auth, firebaseInitialized, appId } from '../constants/firebase'; 
import { setPersistence, inMemoryPersistence, signInAnonymously } from 'firebase/auth';
import { collection, addDoc, doc, onSnapshot, getDocs, query, where } from 'firebase/firestore';

const IntakeForm = () => {
    const defaultSettings = {
        askEmail: true, 
        askAddress: false, 
        askClub: true, 
        askCurrentString: false, 
        allowMultipleRackets: true 
    };

    const [settings, setSettings] = useState<any>(defaultSettings);
    const [realUserId, setRealUserId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>(''); 
    
    const [companyLogo, setCompanyLogo] = useState<string>('');
    
    const [customerData, setCustomerData] = useState({ name: '', phone: '', email: '', address: '', club: '' });
    const [generalData, setGeneralData] = useState({ deadline: '', deadlineTime: '' });
    
    const [hasMultiple, setHasMultiple] = useState(false);
    const [racketCount, setRacketCount] = useState(1);
    const [areIdentical, setAreIdentical] = useState(true);

    const [globalOwnString, setGlobalOwnString] = useState(false);
    
    const defaultRacket = { racket: '', currentString: '', string: '', isHybrid: false, crossString: '', tensionMain: '', tensionCross: '' };
    const [rackets, setRackets] = useState([{...defaultRacket}]);

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);

    const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const targetUserId = searchParams.get('id');

    useEffect(() => {
        document.documentElement.classList.add('dark');
        
        let unsubscribe: (() => void) | undefined;
        
        const init = async () => {
            if (!targetUserId) {
                setLoading(false);
                return;
            }

            if (firebaseInitialized && auth) {
                try {
                    await setPersistence(auth, inMemoryPersistence);
                    await signInAnonymously(auth);
                } catch (error) {
                    console.warn("Anoniem inloggen fallback geactiveerd.");
                }
            }

            try {
                let docRefToListen = null;

                if (targetUserId.length >= 20) {
                    docRefToListen = doc(db, 'artifacts', appId, 'users', targetUserId);
                } else {
                    const q = query(collection(db, 'artifacts', appId, 'users'), where('customLinkId', '==', targetUserId.toLowerCase()));
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        docRefToListen = doc(db, 'artifacts', appId, 'users', querySnapshot.docs[0].id);
                    }
                }

                if (docRefToListen) {
                    setRealUserId(docRefToListen.id);
                    
                    unsubscribe = onSnapshot(docRefToListen, (docSnap) => {
                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            setSettings({ ...defaultSettings, ...(data.formSettings || {}) });
                            setCompanyLogo(data.companyLogo || '');
                        }
                        setLoading(false);
                    }, (err) => {
                        console.error("Live listener error", err);
                        setSettings(defaultSettings); 
                        setLoading(false);
                    });
                } else {
                    setRealUserId('invalid');
                    setLoading(false);
                }
            } catch (err: any) {
                console.error("Fout tijdens ophalen document link:", err);
                setRealUserId('invalid');
                setErrorMessage(err.message);
                setLoading(false);
            }
        };
        
        init();

        return () => {
            if (localStorage.getItem('theme') !== 'dark') {
                document.documentElement.classList.remove('dark');
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [targetUserId]);

    const toggleMultipleRackets = () => {
        const nextState = !hasMultiple;
        setHasMultiple(nextState);
        if (nextState && racketCount === 1) {
            setRacketCount(2);
            if (rackets.length < 2) {
                setRackets([...rackets, {...defaultRacket}]);
            }
        }
    };

    const handleCountChange = (e: any) => {
        const num = parseInt(e.target.value);
        setRacketCount(num);
        if (num > rackets.length) {
            const newRackets = [...rackets];
            for (let i = rackets.length; i < num; i++) newRackets.push({...defaultRacket});
            setRackets(newRackets);
        }
    };

    const updateRacket = (index: number, field: string, value: any) => {
        const newRackets = [...rackets];
        newRackets[index] = { ...newRackets[index], [field]: value };
        setRackets(newRackets);
    };

    const copyFirstRacketSettings = () => {
        const newRackets = [...rackets];
        for (let i = 1; i < newRackets.length; i++) newRackets[i] = { ...newRackets[0] };
        setRackets(newRackets);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!realUserId || realUserId === 'invalid') return;
        
        setLoading(true);
        try {
            const finalRacketCount = hasMultiple ? racketCount : 1;
            
            for (let i = 0; i < finalRacketCount; i++) {
                const rData = (hasMultiple && areIdentical) ? rackets[0] : rackets[i];
                
                await addDoc(collection(db, 'artifacts', appId, 'users', realUserId, 'inbox'), {
                    name: customerData.name,
                    phone: customerData.phone,
                    email: customerData.email,
                    address: customerData.address,
                    club: customerData.club,
                    deadline: generalData.deadline,
                    deadlineTime: generalData.deadlineTime,
                    ownString: globalOwnString,
                    ...rData,
                    createdAt: Date.now() + i
                });
            }
            setSubmitted(true);
        } catch (error) {
            console.error("Firestore Write Error:", error);
            alert("Er is iets misgegaan bij het versturen. Probeer het opnieuw.");
        }
        setLoading(false);
    };

    if (loading) return <div className="dark min-h-screen flex flex-col items-center justify-center bg-slate-950 p-4 text-white"><Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" /><p>Formulier laden...</p></div>;

    if (submitted) return (
        <div className="dark min-h-screen flex items-center justify-center bg-slate-950 p-4">
            <div className="bg-slate-800 p-8 rounded-3xl shadow-xl max-w-md w-full text-center border border-slate-700"><CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" /><h2 className="text-2xl font-bold mb-2 text-white">Ontvangen!</h2><p className="text-slate-300">Je aanvraag is succesvol doorgestuurd. Lever je racket in zoals aangegeven op jouw locatie.</p></div>
        </div>
    );

    if (realUserId === 'invalid') return (
        <div className="dark min-h-screen flex items-center justify-center bg-slate-950 p-4 text-center">
            <div className="bg-slate-800 p-8 rounded-3xl shadow-xl max-w-md w-full border border-red-900/30">
                <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2 text-white">Ongeldige Link</h2>
                <p className="text-slate-300 mb-4">Deze link bestaat niet of is verlopen.</p>
                {errorMessage && <div className="bg-slate-900/50 p-3 rounded-lg text-xs text-red-400 font-mono text-left break-words">{errorMessage}</div>}
            </div>
        </div>
    );

    // Toegevoegd: isIdenticalMultiple parameter voor betere visuele feedback
    const renderRacketBlock = (rData: typeof defaultRacket, index: number, isSingle: boolean, isIdenticalMultiple = false) => (
        <div key={index} className={`space-y-4 ${!isSingle ? 'p-5 bg-slate-900/50 rounded-2xl border border-slate-700/50 relative overflow-hidden mt-6' : 'mt-4'}`}>
            {!isSingle && <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500" />}
            {!isSingle && <h3 className="font-bold text-indigo-400">Racket {index + 1}</h3>}
            
            {/* Visuele feedback als het formulier voor meerdere identieke rackets geldt */}
            {isIdenticalMultiple && (
                <div className="mb-2 p-3 bg-indigo-500/20 border border-indigo-500/50 rounded-xl text-indigo-300 text-sm font-bold flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> 
                    Deze gegevens gelden voor alle {racketCount} rackets
                </div>
            )}

            <div>
                <label className="block text-sm font-bold text-slate-300 mb-1">Merk & Model Racket</label>
                <input type="text" required className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" placeholder="bijv. Yonex VCORE 98" value={rData.racket} onChange={e => updateRacket(index, 'racket', e.target.value)} />
            </div>

            {settings?.askCurrentString && (
                <div className="animate-in fade-in slide-in-from-top-2">
                    <label className="block text-sm font-bold text-slate-300 mb-1">Huidige / Oude Snaar (Optioneel)</label>
                    <input type="text" className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" placeholder="Wat zit er nu in?" value={rData.currentString} onChange={e => updateRacket(index, 'currentString', e.target.value)} />
                </div>
            )}

            <div className="space-y-4 pt-2">
                <div>
                    <label className="block text-sm font-bold text-slate-300 mb-1">{globalOwnString ? "Welke snaar breng je mee?" : "Gewenste Snaar"}</label>
                    <input type="text" required className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" placeholder={globalOwnString ? "Merk & Type" : "bijv. Alu Power (of typ 'Advies')"} value={rData.string} onChange={e => updateRacket(index, 'string', e.target.value)} />
                </div>

                <div className="flex items-center gap-3" onClick={() => updateRacket(index, 'isHybrid', !rData.isHybrid)}>
                    <input type="checkbox" checked={rData.isHybrid} readOnly className="w-4 h-4 text-indigo-600 rounded bg-slate-900 border-slate-700" />
                    <label className="text-sm font-bold text-slate-400 cursor-pointer">Hybride bespanning (2 snaren)</label>
                </div>

                {rData.isHybrid && (
                    <div className="animate-in fade-in slide-in-from-top-2">
                        <label className="block text-sm font-bold text-slate-300 mb-1">Breedte Snaar (Cross)</label>
                        <input type="text" required className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" placeholder="bijv. Excel of 'Zelfde als lengte'" value={rData.crossString} onChange={e => updateRacket(index, 'crossString', e.target.value)} />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                    <label className="block text-sm font-bold text-slate-300 mb-1">Kg Lengte</label>
                    <input type="number" step="0.5" required className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" placeholder="24" value={rData.tensionMain} onChange={e => updateRacket(index, 'tensionMain', e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-bold text-slate-300 mb-1">Kg Breedte</label>
                    <input type="number" step="0.5" required className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" placeholder="24" value={rData.tensionCross} onChange={e => updateRacket(index, 'tensionCross', e.target.value)} />
                </div>
            </div>
            
            {!isSingle && index === 0 && !areIdentical && racketCount > 1 && (
                <button type="button" onClick={copyFirstRacketSettings} className="w-full mt-2 p-2 bg-indigo-500/10 text-indigo-400 rounded-lg text-sm font-bold flex items-center justify-center hover:bg-indigo-500/20 transition">
                    <Copy className="w-4 h-4 mr-2" /> Kopieer racket, snaar & kilo's naar de rest
                </button>
            )}
        </div>
    );

    return (
        <div className="dark min-h-screen bg-slate-950 py-10 px-4 font-sans flex flex-col justify-start items-center text-white">
            <div className="bg-slate-800 p-6 md:p-8 rounded-3xl shadow-xl max-w-lg w-full h-fit border border-slate-700">
                <div className="text-center mb-8">
                    <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                        <img 
                            src={companyLogo || "/logo.png"} 
                            alt="Logo" 
                            className="w-full h-full object-contain" 
                            onError={(e: any) => { 
                                e.target.style.display = 'none'; 
                                if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; 
                            }} 
                        />
                        <div className="hidden bg-indigo-600 w-16 h-16 rounded-full items-center justify-center shadow-lg">
                            <Scissors className="w-8 h-8 text-white" />
                        </div>
                    </div>
                    <h1 className="text-2xl font-bold text-white">Bespanning Aanvragen</h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4 pb-6 border-b border-slate-700">
    
    {/* Rij 1: Naam & Telefoon */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
            <label className="block text-sm font-bold text-slate-300 mb-1">Jouw Naam</label>
            <input type="text" required autoComplete="name" className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={customerData.name} onChange={e => setCustomerData({...customerData, name: e.target.value})} placeholder="Voor- en achternaam" />
        </div>
        <div>
            <label className="block text-sm font-bold text-slate-300 mb-1">Telefoonnummer</label>
            <input type="tel" required autoComplete="tel" className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={customerData.phone} onChange={e => setCustomerData({...customerData, phone: e.target.value})} placeholder="06-12345678" />
        </div>
    </div>

    {/* Rij 2: E-mailadres & Tennisclub */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {settings?.askEmail && (
            <div className="animate-in fade-in slide-in-from-top-2">
                <label className="block text-sm font-bold text-slate-300 mb-1">E-mailadres</label>
                <input type="email" required autoComplete="email" className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={customerData.email} onChange={e => setCustomerData({...customerData, email: e.target.value})} placeholder="jouw@email.nl" />
            </div>
        )}
        
        {settings?.askClub && (
            <div className="animate-in fade-in slide-in-from-top-2">
                <label className="block text-sm font-bold text-slate-300 mb-1">Tennisclub</label>
                <input type="text" className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={customerData.club} onChange={e => setCustomerData({...customerData, club: e.target.value})} placeholder="(Optioneel)" />
            </div>
        )}
    </div>

    {/* Rij 3 (Optioneel): Adres - Deze staat mooi over de volle breedte */}
    {settings?.askAddress && (
        <div className="animate-in fade-in slide-in-from-top-2">
            <label className="block text-sm font-bold text-slate-300 mb-1">Straatnaam, Huisnummer & Woonplaats</label>
            <input type="text" required autoComplete="street-address" className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={customerData.address} onChange={e => setCustomerData({...customerData, address: e.target.value})} placeholder="Straat 1, Woonplaats" />
        </div> 
    )}
</div>

                    <div className="flex items-center gap-3 p-4 bg-slate-900 rounded-xl border border-slate-700 cursor-pointer hover:border-indigo-500 transition" onClick={() => setGlobalOwnString(!globalOwnString)}>
                        <input type="checkbox" checked={globalOwnString} readOnly className="w-5 h-5 text-indigo-600 rounded bg-slate-800 border-slate-600 focus:ring-indigo-500" />
                        <label className="text-sm font-bold text-slate-200 cursor-pointer">Ik lever mijn eigen snaren aan</label>
                    </div>

                    {settings?.allowMultipleRackets && (
    <div className="bg-slate-900/80 border border-slate-700 rounded-2xl p-4 animate-in fade-in slide-in-from-top-2 mt-4">
        <div className="flex items-center justify-between cursor-pointer" onClick={toggleMultipleRackets}>
            <span className="font-bold text-indigo-300">Ik lever meerdere rackets in</span>
            <input type="checkbox" checked={hasMultiple} readOnly className="w-5 h-5 text-indigo-600 rounded bg-slate-800 border-slate-600 focus:ring-indigo-500" />
        </div>
        
        {hasMultiple && (
            <div className="mt-4 pt-4 border-t border-slate-700/50 animate-in fade-in slide-in-from-top-2">
                <label className="block text-sm font-bold text-slate-300 mb-2">Hoeveel rackets wil je bespannen?</label>
                <select value={racketCount} onChange={handleCountChange} className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white outline-none mb-4">
                    <option value={2}>2 Rackets</option>
                    <option value={3}>3 Rackets</option>
                    <option value={4}>4 Rackets</option>
                    <option value={5}>5 Rackets</option>
                </select>

                <label className="block text-sm font-bold text-slate-300 mb-2">Krijgen al deze rackets dezelfde snaar & spanning?</label>
                <div className="flex gap-2">
                    <button type="button" onClick={() => setAreIdentical(true)} className={`flex-1 py-2 rounded-lg font-bold text-sm transition border ${areIdentical ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}>Ja, exact hetzelfde</button>
                    <button type="button" onClick={() => setAreIdentical(false)} className={`flex-1 py-2 rounded-lg font-bold text-sm transition border ${!areIdentical ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}>Nee, verschillend</button>
                </div>
            </div>
        )}
    </div>
)}

                    <div>
                        {/* Aangepaste logica: geef de isIdenticalMultiple (true/false) flag mee voor heldere feedback */}
                        {(!hasMultiple) 
                            ? renderRacketBlock(rackets[0], 0, true) 
                            : (hasMultiple && areIdentical) 
                                ? renderRacketBlock(rackets[0], 0, true, true) 
                                : rackets.slice(0, racketCount).map((rData, i) => renderRacketBlock(rData, i, false))}
                    </div>

                   <div className="space-y-2 pt-6 border-t border-slate-700">
    <label className="block text-sm font-bold text-slate-300">Wanneer moet het uiterlijk klaar zijn? <span className="text-slate-500 font-normal">(Optioneel)</span></label>
    <div className="flex gap-3">
        {/* 'required' is bij beide velden weggehaald */}
        <input type="date" className="flex-1 p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={generalData.deadline} onChange={e => setGeneralData({...generalData, deadline: e.target.value})} />
        <input type="time" className="w-28 p-3 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-indigo-500" value={generalData.deadlineTime} onChange={e => setGeneralData({...generalData, deadlineTime: e.target.value})} />
    </div>
</div>

                    <button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-xl mt-6 flex justify-center items-center transition shadow-lg shadow-indigo-500/30">
                        {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (hasMultiple ? `Aanvraag voor ${racketCount} rackets versturen` : "Aanvraag Versturen")}
                    </button>
                </form>
            </div>
            
            <div className="mt-8 text-center text-slate-500 text-xs flex items-center justify-center gap-4">
                <a href="/support/voorwaarden" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400 transition">Algemene Voorwaarden</a>
                <span>&bull;</span>
                <a href="/support/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400 transition">Privacy Verklaring</a>
            </div>
        </div>
    );
};
export default IntakeForm;